// charts
import Chart from 'chart.js'
import moment from 'moment'

// components
import maPaginator from '@/components/paginator'
import maInputSelect from '@/components/input/modules/select'
import maDatePicker from '@/components/datePicker'
import maLoader from '@/components/loaderSmall'
import maEmpty from '@/components/empty'

// utils
import downloadBlobFileXML from '@/utils/downloadBlobFileXML'

// api
import {
  apiGetGagDashboard,
  apiGetGagDashboardDocument
} from '@/services/API/dashboard'

const config = {
  legend: {
    display: false
  },
  tooltips: {
    enabled: false
  },
  elements: {
    line: {
      tension: 0
    }
  },
  layout: {
    padding: {
      top: 5,
      bottom: 5,
      left: 5,
      right: 5
    }
  },
  scales: {
    xAxes: [{
      display: false
    }],
    yAxes: [{
      display: false
    }]
  },
  responsive: true, // Instruct chart js to respond nicely.
  maintainAspectRatio: false // Add to prevent default behaviour of full-width/height
}

export default {
  name: 'dashboardGAG',
  data: function () {
    return {
      selectedWeeks: [],
      gagGraph: null,
      countGraph: null,
      GAGData: {},
      filtersData: {
        currentPage: 1,
        totalPages: 0,
        orderBy: null,
        toDate: null,
        fromDate: null
      },
      kpis: {
        gag: null,
        gagPercent: null,
        TE: null,
        gagUser: null,
        gagUserPercent: null,
        gagSinister: null,
        gagSinisterPercent: null
      },
      orderByOptions: [],
      startDate: null,
      endDate: null,
      maxDate: null,
      minDate: null,
      today: new Date(),
      isLoaderActive: true,
      disabledStartDays: [1, 3, 4, 5, 6, 7],
      disabledEndDays: [2, 3, 4, 5, 6, 7],
      defaultDateStart: null,
      defaultDateEnd: null
    }
  },
  components: {
    maPaginator,
    maInputSelect,
    maDatePicker,
    maLoader,
    maEmpty
  },
  computed: {
    hasData: function () {
      return this.GAGData?.rows?.length > 0
    },
    gagKpi: function () {
      return this.kpis.gag !== null && this.kpis.gagPercent !== null ? `${this.kpis.gag} / ${this.kpis.gagPercent}%` : '-'
    },
    gagUserKpi: function () {
      return this.kpis.gagUser !== null && this.kpis.gagUserPercent !== null ? `${this.kpis.gagUser} / ${this.kpis.gagUserPercent}%` : '-'
    },
    gagSinisterKpi: function () {
      return this.kpis.gagSinister !== null && this.kpis.gagSinisterPercent !== null ? `${this.kpis.gagSinister} / ${this.kpis.gagSinisterPercent}%` : '-'
    },
    cleanData () {
      return (this.endDate > this.maxDate || this.endDate < this.startDate)
    }
  },
  methods: {
    handleCreateChart: function () {
      if (!this.GAGData) return
      this.GAGData.rows.forEach((row, index) => {
        const weekData = row.weeks_data
        const weeksGagCount = weekData.map((week) => week.sinister_gag_count)
        const weeksCount = weekData.map((week) => week.sinister_count)

        if (weekData.length === 1) return

        // total GAG chart
        const gagGraphCanvas = document.querySelector(`#gag-count-graph_${index}`)

        this.gagGraph = new Chart(gagGraphCanvas, {
          type: 'line',
          data: {
            labels: weeksGagCount.map(() => ''),
            datasets: [{
              data: weeksGagCount,
              fill: false,
              borderColor: '#003A69',
              backgroundColor: '#003A69',
              borderWidth: 2,
              pointRadius: 2
            }] },
          options: { ...config }
        })

        // total GAG chart
        const countGraphCanvas = document.querySelector(`#count-graph_${index}`)
        this.countGraph = new Chart(countGraphCanvas, {
          type: 'line',
          data: {
            labels: weeksCount.map(() => ''),
            datasets: [{
              data: weeksCount,
              fill: false,
              borderColor: '#003A69',
              backgroundColor: '#003A69',
              borderWidth: 2,
              pointRadius: 2
            }] },
          options: { ...config }
        })
      })
    },
    getData: function (callback) {
      apiGetGagDashboard({
        fromDate: moment(Object(this.startDate)).format('YYYY-MM-DD'),
        toDate: moment(Object(this.endDate)).format('YYYY-MM-DD'),
        page: this.filtersData.currentPage,
        orderBy: this.filtersData.orderBy
      })
        .then((data) => {
          this.isLoaderActive = false
          this.GAGData = data
          this.filtersData.totalPages = this.GAGData.total_pages
          this.selectedWeeks = this.GAGData.week_numbers
          this.kpis.gag = this.GAGData.complete_gag_total
          this.kpis.gagPercent = this.GAGData.complete_gag_total_percentage
          this.kpis.TE = this.GAGData.complete_sinister_total
          this.kpis.gag = this.GAGData.complete_gag_total
          this.kpis.gagPercent = this.GAGData.complete_gag_total_percentage
          this.kpis.gagUser = this.GAGData.gag_user_total
          this.kpis.gagUserPercent = this.GAGData.gag_user_total_percentage
          this.kpis.gagSinister = this.GAGData.sinister_user_total
          this.kpis.gagSinisterPercent = this.GAGData.sinister_user_total_percentage
          if (callback) callback()
        })
        .catch((err) => {
          const errs = [400, 500, 409]
          const errCode = errs.find(errCode => err.response.status === errCode) || 0
          this.throwNotification(this.$t(`notifications.error.${errCode}.title`), this.$t(`notifications.error.${errCode}.body`), 'KO')
          this.resetViewData()
          this.isLoaderActive = false
        })
    },
    resetViewData: function () {
      this.GAGData = {}
      this.selectedWeeks = []
      this.kpis = {
        gag: null,
        gagPercent: null,
        TE: null,
        gagUser: null,
        gagUserPercent: null,
        gagSinister: null,
        gagSinisterPercent: null
      }
    },
    handleViewData: function () {
      this.isLoaderActive = true

      this.getData(() => {
        this.$nextTick(() => {
          this.handleCreateChart()
          this.isLoaderActive = false
        })
      })
    },
    handlePageUpdate: function (page) {
      this.filtersData.currentPage = page
      this.handleViewData()
    },
    handlePageSet: function (page) {
      this.filtersData.currentPage = page
    },
    handleOrderByFilterChange: function (selectedValue) {
      this.filtersData.orderBy = selectedValue
      this.filtersData.currentPage = 1
      this.handleViewData()
    },
    setOrderByFilterOptions: function () {
      this.orderByOptions = [
        {
          key: 'tmo',
          literal: this.$t('dashboard.GAG.filters.orderByOptions.tmo')
        },
        {
          key: 'gag_total',
          literal: this.$t('dashboard.GAG.filters.orderByOptions.totalGAG')
        },
        {
          key: 'sinister_total',
          literal: this.$t('dashboard.GAG.filters.orderByOptions.sinisterTotal')
        },
        {
          key: 'conversion',
          literal: this.$t('dashboard.GAG.filters.orderByOptions.conversion')
        }
      ]
    },

    getDocument () {
      apiGetGagDashboardDocument({
        fromDate: moment(Object(this.startDate)).format('YYYY-MM-DD'),
        toDate: moment(Object(this.endDate)).format('YYYY-MM-DD'),
        page: this.filtersData.currentPage,
        orderBy: this.filtersData.orderBy
      })
        .then(data => {
          downloadBlobFileXML(data, 'Dashboard data.xlsx')
        })
    },

    getCalcMaxMinDate (date) {
      const weekMiliSec = 1000 * 60 * 60 * 24 * 28
      const calc = date.getTime() + weekMiliSec
      const result = new Date(calc)
      this.minDate = date
      const today = new Date()
      const todayWeek = today.getDay()
      if (today.getDay() !== 0) today.setDate(today.getDate() - todayWeek)
      const todayMax = new Date(today.setDate(today.getDate() + 1))
      this.maxDate = (result > todayMax) ? todayMax : result
    },

    handleChangeInputStartDate (value) {
      this.startDate = value
      this.endDate = null
      this.getCalcMaxMinDate(value)
    },
    handleChangeInputEndDate (value) {
      this.endDate = value
      this.handleViewData()
    },

    // - Default date
    defaultDates () {
      const weekMiliSec = 1000 * 60 * 60 * 24 * 27
      const today = new Date()
      // - Start
      const calc = today.getTime() - weekMiliSec
      const todayStart = new Date(calc)
      const todayWeek = todayStart.getDay()
      if (todayStart.getDay() !== 1) todayStart.setDate(todayStart.getDate() - (todayWeek - 1))
      this.startDate = todayStart
      this.defaultDateStart = moment(Object(todayStart)).format('YYYY-MM-DD')

      // - End
      const todayEnd = todayStart.getTime() + weekMiliSec
      const todayWeekEnd = today.getDay()
      if (today.getDay() !== 0) today.setDate(today.getDate() - todayWeekEnd)
      const newDateMax = (new Date(todayEnd) > today) ? today : new Date(todayEnd)
      this.maxDate = new Date(newDateMax.setDate(newDateMax.getDate() + 1))
      this.endDate = new Date(todayEnd)
      const date = new Date(todayEnd)
      this.defaultDateEnd = moment(Object(date)).format('YYYY-MM-DD')
    }

  },
  mounted: function () {
    this.defaultDates()
    this.setOrderByFilterOptions()
    this.handleViewData()
  }
}
